/**
 * Per-page, per-device transport overrides for <gr-connect>.
 *
 * The HTML author picks a default transport via attributes; the user can
 * switch protocol at runtime from the connect pill. Overrides persist in
 * localStorage keyed by page path + device so a revisit keeps the choice.
 */

import { transportDefinition, transportDefinitions } from "../adapters/adapter";

export type ConnectionOverride = {
  transport: string;
  options: Record<string, string>;
};

export function connectionOptionsFromFields(
  transport: string,
  fields: ArrayLike<{ name: string; value: string }>,
): Record<string, string> {
  const definition = transportDefinition(transport);
  const schema = new Map((definition?.fields ?? []).map((field) => [field.name, field]));
  const options: Record<string, string> = {};
  for (let index = 0; index < fields.length; index += 1) {
    const { name, value } = fields[index];
    const field = schema.get(name);
    if (!field) continue;
    const normalized = field.trim === false ? value : value.trim();
    if (normalized || field.trim === false) options[name] = normalized;
  }
  return options;
}

export function connectionTransports() {
  return transportDefinitions();
}

export function requiresGesture(transport: string): boolean {
  return transportDefinition(transport)?.requiresGesture ?? true;
}

export function overrideKey(pagePath: string, device: string): string {
  return `granrojo:connect:${pagePath}:${device}`;
}

/**
 * Storage key for a device on the current page. `baseURI` rather than
 * `location`, so an edited Studio preview (srcdoc, `about:srcdoc`) keeps
 * the published page's key instead of inventing a new one.
 */
export function pageOverrideKey(device: string): string {
  let pagePath = "unknown";
  try {
    pagePath = new URL(document.baseURI).pathname;
  } catch {
    // Keep the fallback key.
  }
  return overrideKey(pagePath, device);
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function persistentOptions(transport: string, options: Record<string, string>): Record<string, string> {
  const definition = transportDefinition(transport);
  if (!definition) return {};
  const saved: Record<string, string> = {};
  for (const field of definition.fields ?? []) {
    if (!field.persist || typeof options[field.name] !== "string") continue;
    const value = options[field.name];
    if (field.cleanUrl) {
      try {
        const url = new URL(value);
        if (url.username || url.password || url.search || url.hash) continue;
      } catch {
        continue;
      }
    }
    saved[field.name] = value;
  }
  return saved;
}

export function loadOverride(storage: StorageLike, key: string): ConnectionOverride | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.transport !== "string" || obj.transport.length === 0) return null;
    const options: Record<string, string> = {};
    if (typeof obj.options === "object" && obj.options !== null) {
      for (const [k, v] of Object.entries(obj.options)) {
        if (typeof v === "string") options[k] = v;
      }
    }
    // Keep removed/unknown names visible so they cannot silently select another
    // transport. Without a registered schema, none of their options are saved.
    const result = { transport: obj.transport, options: persistentOptions(obj.transport, options) };
    if (JSON.stringify(result.options) !== JSON.stringify(options)) saveOverride(storage, key, result);
    return result;
  } catch {
    return null;
  }
}

export function saveOverride(storage: StorageLike, key: string, override: ConnectionOverride): void {
  try {
    storage.setItem(key, JSON.stringify({
      transport: override.transport,
      options: persistentOptions(override.transport, override.options),
    }));
  } catch {
    // Storage full / disabled — the override still applies for this session.
  }
}

export function clearOverride(storage: StorageLike, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore.
  }
}

/**
 * Merge element attributes with a user override into the effective
 * transport + adapter options. Override options win per key.
 */
export function effectiveConnection(
  attrs: Record<string, string>,
  override: ConnectionOverride | null,
): { transport: string; options: Record<string, string> } {
  const transport = override?.transport ?? attrs.transport ?? "serial";
  return { transport, options: { ...attrs, ...(override?.options ?? {}), transport } };
}
